import { BASE_URL, COMMON_BASE_URL } from '../../../envs'
import { Contract } from '../../../types/contract'
import { Transaction } from '../../../types/donation/transaction'
import { RecurrenceInterval } from '../../../types/recurring-interval'
import { HttpClient } from '../http-client'

export class ContractsClient {
  private readonly httpClient

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient
  }

  async getContracts(charityId: string, recordFilter?: Record<string, any>, page: number = 1, size: number = 25): Promise<Contract[]> {
    const queryParams = new URLSearchParams()

    if (recordFilter) {
      for (const [key, value] of Object.entries(recordFilter)) {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value))
        }
      }
    }
    const url = `${BASE_URL}/charities/${charityId}/contracts?${queryParams.toString()}&page=${page}&size=${size}`

    const response = await this.httpClient.fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      throw new Error(`Error while retrieving contracts: ${response.status}`)
    }
    const data = await response.json()
    return data as Contract[]
  }

  async getContractsById(charityId: string, contractsId: string): Promise<Contract> {
    const url = `${BASE_URL}/charities/${charityId}/contracts/${contractsId}`
    const response = await this.httpClient.fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      throw new Error(`Error while retrieving contract: ${response.status}`)
    }
    const data = await response.json()
    if (data.length === 1) {
      return data[0]
    } else {
      throw new Error('Contract not found')
    }
  }

  async getTransactions(charityId: string, contractId: string): Promise<Transaction[]> {
    const url = `${BASE_URL}/charities/${charityId}/contracts/${contractId}/transactions`
    const response = await this.httpClient.fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      throw new Error(`Error while retrieving transactions: ${response.status}`)
    }
    return await response.json()
  }

  async updateContract(charityId: string, contractId: string, contract: Contract): Promise<Contract> {
    const url = `${BASE_URL}/charities/${charityId}/contracts/${contractId}`

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { _id, ...req } = contract // In order to remove the _id from the request
    const response = await this.httpClient.fetch(url, {
      method: 'PUT',
      body: JSON.stringify(req),
      headers: {
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      throw new Error(`Error while updating contract: ${response.status}`)
    }
    return await response.json()
  }

  async patchContract(charityId: string, contractId: string, value: Partial<Contract>): Promise<Contract> {
    const url = `${BASE_URL}/charities/${charityId}/contracts/${contractId}`

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { _id, ...req } = value // In order to remove the _id from the request
    const response = await this.httpClient.fetch(url, {
      method: 'PATCH',
      body: JSON.stringify(req),
      headers: {
        'Content-Type': 'application/json',
      },
    })
    if (!response.ok) {
      throw new Error(`Error while patching contract: ${response.status}`)
    }
    return await response.json()
  }

  async updateSubscription(
    charityId: string,
    contractId: string,
    value: { stripeSetupIntentId?: string; recurrence?: RecurrenceInterval; selectedDonationAmount?: number; delete?: boolean }
  ): Promise<void> {
    const url = `${BASE_URL}/charities/${charityId}/contracts/${contractId}/subscription`

    const response = await this.httpClient.fetch(url, {
      method: 'POST',
      body: JSON.stringify(value),
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error('Error while updating subscription')
    }
  }

  async finalize(charityId: string, contractId: string): Promise<void> {
    const url = `${BASE_URL}/charities/${charityId}/contracts/${contractId}/finalize`

    const response = await this.httpClient.fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error('Error while activating subscription')
    }
  }

  async refundTransaction(charityId: string, contractId: string, transactionId: string): Promise<void> {
    const url = `${BASE_URL}/charities/${charityId}/contracts/${contractId}/transactions/${transactionId}`

    const response = await this.httpClient.fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error('Error while refunding transaction')
    }
  }

  async intent(charityId: string, contractId: string, isActivation: boolean = false): Promise<string> {
    const url = `${BASE_URL}/charities/${charityId}/contracts/${contractId}/intent?isNew=${isActivation ? 'true' : 'false'}`

    const response = await this.httpClient.fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error('Error while creating intent')
    }

    const { setupIntentSecret } = await response.json()
    return setupIntentSecret
  }

  async getPdf(contractId: string): Promise<Blob> {
    const url = `${COMMON_BASE_URL}/utils/pdf-generator?type=contract&contractId=${contractId}`

    const res = await this.httpClient.fetch(url, {})
    return await res.blob()
  }
}
